import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Send, Unlink, ExternalLink } from 'lucide-react';

export default function ProfilePage() {
  const { t } = useLanguage();
  const { user, refresh } = useAuth();
  const [tgStatus, setTgStatus] = useState(null);
  const [linkCode, setLinkCode] = useState(null);
  const [pwdForm, setPwdForm] = useState({ old_password: '', new_password: '' });

  const loadTg = () => api.get('/profile/telegram/status').then(r => setTgStatus(r.data));
  useEffect(() => { loadTg(); }, []);

  const createLink = async () => {
    try {
      const r = await api.post('/profile/telegram/link');
      setLinkCode(r.data);
      toast.success('Link ready — open Telegram bot');
    } catch { toast.error('Failed'); }
  };
  const unlink = async () => {
    await api.post('/profile/telegram/unlink');
    setLinkCode(null); await loadTg(); await refresh();
    toast.success('Unlinked');
  };

  const changePwd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/change-password', pwdForm);
      toast.success('Password changed');
      setPwdForm({ old_password: '', new_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">{t('wallet.profile')}</h1>

      <Card className="bg-white"><CardContent className="p-6">
        <h2 className="font-serif text-xl mb-4">Personal information</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div><dt className="text-[hsl(var(--muted-foreground))]">{t('common.email')}</dt><dd className="font-medium">{user?.email}</dd></div>
          <div><dt className="text-[hsl(var(--muted-foreground))]">Name</dt><dd className="font-medium">{user?.first_name} {user?.last_name}</dd></div>
          <div><dt className="text-[hsl(var(--muted-foreground))]">Phone</dt><dd className="font-medium">{user?.phone || '—'}</dd></div>
          <div><dt className="text-[hsl(var(--muted-foreground))]">Citizenship</dt><dd className="font-medium">{user?.citizenship || '—'}</dd></div>
          <div><dt className="text-[hsl(var(--muted-foreground))]">Account type</dt><dd className="font-medium capitalize">{user?.account_type}</dd></div>
          <div><dt className="text-[hsl(var(--muted-foreground))]">KYC</dt><dd className="font-medium">{user?.kyc_status}</dd></div>
        </dl>
      </CardContent></Card>

      <Card className="bg-white"><CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Send className="h-5 w-5 text-[hsl(var(--brand-navy))]" />
          <h2 className="font-serif text-xl">Telegram</h2>
        </div>
        {tgStatus?.linked ? (
          <div className="flex items-center justify-between">
            <div className="text-sm">Linked as <span className="font-medium">@{tgStatus.username || tgStatus.chat_id}</span></div>
            <Button variant="outline" size="sm" onClick={unlink}><Unlink className="h-4 w-4 mr-1" />Unlink</Button>
          </div>
        ) : (
          <div>
            {linkCode ? (
              <div className="space-y-3">
                <p className="text-sm">Open the bot and press Start:</p>
                <div className="flex flex-wrap items-center gap-2">
                  <a target="_blank" rel="noopener noreferrer" href={linkCode.deep_link} className="inline-flex items-center gap-2 rounded-xl border bg-[hsl(var(--brand-gold))]/10 border-[hsl(var(--brand-gold))]/40 px-3 py-2 text-sm text-[hsl(var(--brand-navy))]">
                    <ExternalLink className="h-4 w-4" /> {linkCode.deep_link}
                  </a>
                </div>
                <Button size="sm" variant="ghost" onClick={loadTg}>Check status</Button>
              </div>
            ) : (
              <Button size="sm" data-testid="telegram-link-button" onClick={createLink} className="bg-[hsl(var(--brand-navy))]">Generate link</Button>
            )}
          </div>
        )}
      </CardContent></Card>

      <Card className="bg-white"><CardContent className="p-6">
        <h2 className="font-serif text-xl mb-4">Change password</h2>
        <form onSubmit={changePwd} className="space-y-3 max-w-md">
          <div><Label>Current password</Label><Input required type="password" value={pwdForm.old_password} onChange={e => setPwdForm({ ...pwdForm, old_password: e.target.value })} /></div>
          <div><Label>New password</Label><Input required type="password" minLength={8} value={pwdForm.new_password} onChange={e => setPwdForm({ ...pwdForm, new_password: e.target.value })} /></div>
          <Button type="submit" className="bg-[hsl(var(--brand-navy))]">Update</Button>
        </form>
      </CardContent></Card>
    </div>
  );
}
